/**
 * MULTILEADER entity → `RenderableEntity` adapter (TYPED).
 *
 * DEFUSE Lane H / S9 — typed sibling of
 * `../../dimensions/multileaderToPrimitives.ts`. Reads the canonical
 * `MultileaderEntity` fields directly off the model (`model.ts`:
 * `multileaderEntitySchema` — `leaderRoots` / `contentType` /
 * `textContents` / `textHeight` / `textColor` / `arrowheadSize` /
 * `blockName` / `blockScale` / `blockRotation` / `textLocation` /
 * `blockAttributeReferences` / `annotativeContexts`) instead of the legacy
 * `EntityPreview.properties` string bag, then feeds the SAME shared builder
 * (`../geom/multileaderGeometry.ts`) to produce a byte-identical
 * `RenderableEntity` (golden-parity) for the same entity.
 *
 * # NOT registered in `entityToRenderableTyped`'s switch
 *
 * Unlike every other typed adapter, this one is dispatched EXPLICITLY by
 * `typedFirstRenderable.ts` (`EXPLICIT_DISPATCH_RENDER_KINDS`) and
 * `scene/SceneBulkLoad.ts`, NOT wired into `typed/index.ts`. Reason: block-
 * content flatten (`flattenBlockReference`, `./blockFromEntity.ts`) itself
 * dispatches each block child back through `entityToRenderableTyped` — so
 * registering THIS module in that same switch would close an import cycle
 * (`typed/index.ts` → this file → `../geom/multileaderGeometry.ts` →
 * `./blockFromEntity.ts` → `typed/index.ts`), forbidden by the
 * dependency-cruiser zero-cycle gate. Codex PR #784 round 4 F1 REMOVED the
 * `multileaderGeometry.ts → blockFromEntity.ts` edge of that chain (the block
 * flatten is now INJECTED as a `BlockContentFlattener` — see
 * `./blockReferenceFlatten.ts`), which is what lets `blockFromEntity.ts`
 * import THIS module for its block-child MULTILEADER arm; the explicit
 * dispatch below stays, because a `typed/index.ts` edge would re-close the
 * cycle through that new import. `blockFromEntity.ts`'s own doc
 * comment documents the identical constraint for "Block" (`typed/index.ts`
 * doesn't import it either, for the same reason). Still a REAL typed
 * adapter — no bag round-trip — just reached via a different seam.
 *
 * # MLEADERSTYLE-table resolution — collapses to the entity's own value
 *
 * The legacy bag adapter's `readMlNum`/`readMlStr`/`readMlBool` helpers
 * fall back to a `MLVAR_*` (style-table) bag key when the matching
 * `propertyOverrideFlags` bit is unset — but the render feed builder
 * (`state/dxfGeometry.ts` `buildMultileaderPreview`) never actually WRITES
 * the `MLVAR_*` half of the bag (only `resolveMlVarProperties`, consumed by
 * the Properties-panel translator, does — see that function's call site in
 * `propertyTranslator.ts`). So in the LIVE bag render, EVERY
 * `readMlNum`/`readMlStr`/`readMlBool` call collapses to the ENTITY's own
 * `MLENT_*` value regardless of the override-flag bit — confirmed by
 * tracing each call site's `(flagOn ? entKey : styKey)` / fallback-to-
 * `entKey` branch: the `styKey` half is ALWAYS absent, so the entity value
 * always wins. This adapter therefore reads `entity.*` fields directly,
 * with NO propertyOverrideFlags gating and NO MLEADERSTYLE table lookup —
 * reproducing the collapsed behaviour exactly, not "fixing" a gap that is
 * out of Lane H's declared parity-only scope (tracked as a follow-up, same
 * class as `dimensionFromEntity.ts`'s DIMENSION-annotation-scale gap).
 *
 * The one place this collapse is NOT a pure no-op: the bag's `readMlStr`
 * falls to the call site's literal fallback (which happens to equal the
 * schema default in every case) when the entity's OWN string field is
 * itself empty — `stringOr` below reproduces that for the 3 string fields
 * where it could matter (`arrowheadBlock` / `blockName` / `textColor`).
 */

import {
  safeRecordLookup,
  type BlockDefinition,
  type MultileaderEntity,
} from "@dxf-ai-editor/drawing-model";

import { pickAnnotativeContext } from "../../../scene/renderMultileader";
import type { RenderableEntity } from "../../types";

import {
  buildRenderableShell,
  resolveShellColorHex,
  type AdapterContext,
} from "../adapterContext";
import type { BlockContentFlattener } from "./blockReferenceFlatten";
import {
  buildMultileaderGeometry,
  mapBlockAttributeReferences,
  toMultileaderSceneGeometry,
  type MultileaderRawFields,
  type RawLeaderRoot,
} from "../geom/multileaderGeometry";

const EMPTY_BLOCKS: Record<string, BlockDefinition> = {};

/** Mirrors `readMlStr`'s "empty entity string falls to fallback" collapse. */
function stringOr(value: string, fallback: string): string {
  return value ? value : fallback;
}

// GitHub Codex review PR #558 (post-rebase fresh pass) P1 — MULTILEADER
// `textRotation` (model layer) is RADIANS; the render feed is DEGREES. See
// the `raw.textRotation` doc comment below for the full evidence.
function radToDeg(rad: number): number {
  return (rad * 180) / Math.PI;
}

function mapTypedLeaderRoots(
  roots: MultileaderEntity["leaderRoots"],
): RawLeaderRoot[] {
  return roots.map((root) => ({
    nodes: root.nodes.map((node) => ({
      doglegVector: node.doglegVector
        ? { x: node.doglegVector.x, y: node.doglegVector.y }
        : null,
      lines: node.lines.map((line) => ({
        vertices: line.vertices.map((v) => ({ x: v.x, y: v.y })),
        arrowheadBlock: line.arrowheadBlock || "",
        // S9b — typed `LeaderLine.breakPoints` (CONTEXT_DATA 11/21/31 start +
        // 12/22/32 end) → the shared `LeaderBreak` shape so the WebGL walker
        // cuts the leader stroke at the same gap the SVG renderer draws
        // (Codex P2). Drops z (break gaps are 2-D along the planar leader).
        breaks: (line.breakPoints ?? []).map((bp) => ({
          start: { x: bp.start.x, y: bp.start.y },
          end: { x: bp.end.x, y: bp.end.y },
        })),
      })),
    })),
  }));
}

/** Typed adapter for the `Multileader` entity (`multileaderEntitySchema`). */
export function multileaderFromEntity(
  entity: MultileaderEntity,
  ctx: AdapterContext,
  /**
   * Codex PR #784 round 4 F1 — the block-content flatten, INJECTED (never
   * imported: that edge is the import cycle documented above). REQUIRED, so a
   * new call site cannot silently drop a `contentType === 1` callout's block
   * graphics. Top-level dispatchers pass `flattenBlockReference` verbatim; the
   * block-child arm (`./blockChildMultileader.ts`) passes a closure carrying
   * the enclosing flatten's recursion guard.
   */
  flattenBlockContent: BlockContentFlattener,
): RenderableEntity {
  const blocks = ctx.blocks ?? EMPTY_BLOCKS;

  // PR-14 — "is annotative" is derived from contexts.length > 0 (no stored
  // boolean flag), mirrors `MLENT_ANNOTATIVE_ENABLED` in the bag producer.
  const annotative = (entity.annotativeContexts?.length ?? 0) > 0;
  const annoCtx = annotative
    ? pickAnnotativeContext(entity.annotativeContexts, ctx.annotationScale ?? null)
    : null;

  const contents = entity.textContents || `MLeader ${entity.id}`;

  const raw: MultileaderRawFields = {
    leaderRoots: mapTypedLeaderRoots(entity.leaderRoots ?? []),
    // `leaderType` is never overridden by an annotative context (the
    // context shape carries no leader-type field) — both legacy ternary
    // branches collapse to the entity's own value, so this reads it
    // directly.
    leaderType: Math.round(entity.leaderLineType),
    arrowheadBlockFallback: stringOr(entity.arrowheadBlock, ""),
    arrowheadSize: annoCtx?.arrowheadSize ?? entity.arrowheadSize,
    landingEnabled: entity.landingEnabled,
    doglegEnabled: entity.doglegEnabled,
    doglegLength: annoCtx?.doglegLength ?? entity.doglegLength,
    landingGap: annoCtx?.landingGap ?? entity.landingGap,
    contentType: Math.round(entity.contentType),
    textHeight: annoCtx?.textHeight ?? entity.textHeight,
    // GitHub Codex review PR #558 (post-rebase fresh pass) P1 — the model
    // field is RADIANS (`mirrorRotationFieldRadians`'s doc has the two
    // independent confirmations), but the SHARED render pipeline this raw
    // field feeds (`multileaderGeometry.ts` → `mtext-content.rotation` →
    // the SAME `SceneGeometry.mtext.rotation` real MTEXT populates, which
    // `sceneTextMeshFactories.ts` consumes as DEGREES —
    // `mesh.rotation.z = (g.rotation * Math.PI) / 180`, matching how
    // `mtextFromEntity.ts` passes its OWN (degrees) `entity.rotation`
    // straight through with no conversion) expects DEGREES. Convert at
    // this adapter boundary — the model/storage layer (and MIRROR's
    // `mirrorRotationFieldRadians`) stays radians; only the render feed
    // converts.
    textRotation: radToDeg(annoCtx?.textRotation ?? entity.textRotation),
    // GitHub Codex review PR #558 P2 — handedness is a document-wide MIRROR
    // property of the entity, not a per-scale annotative override (no
    // annotative-context schema field for it — `mleaderContextDataSchema`
    // carries only size/rotation/color overrides, matching how AutoCAD's
    // own annotative scaling never mirrors/flips glyphs per scale). Always
    // reads the entity's own flat field, regardless of the active context.
    textMirrored: entity.textMirrored ?? false,
    textColorRaw: annoCtx?.textColor ?? stringOr(entity.textColor, "ByLayer"),
    // Codex S9b P2 — MLEADER block-content colour (group 93). Threaded into the
    // block-content flatten so BYBLOCK children inherit it (see
    // `buildMultileaderGeometry`). Model default "ByBlock" matches the bag path.
    blockColor: stringOr(entity.blockColor, "ByBlock"),
    blockContentName: stringOr(entity.blockName, ""),
    blockScale: entity.blockScale,
    blockRotation: entity.blockRotation,
    textLocationRaw: { x: entity.textLocation.x, y: entity.textLocation.y },
    contents,
  };

  // Resolve color via shell builder so BYLAYER/BYBLOCK chain runs (typed
  // overload — no bag round-trip).
  const colorHex = resolveShellColorHex(entity, ctx);

  const result = buildMultileaderGeometry({
    raw,
    colorHex,
    flattenBlockContent,
    // Codex round-6 P2 — typed model resolves layer as `entity.layer ?? "0"`
    // (mirrors `buildRenderableShell`); threaded so layer-0 block children
    // inherit the MLEADER layer.
    layer: entity.layer ?? "0",
    // Codex round-7 P2 — the MLEADER's OWN linetype token, threaded so a BYBLOCK
    // block-content child inherits the callout's linetype (see
    // `buildMultileaderGeometry`). Typed model reads `entity.linetype` directly
    // (schema default "ByLayer"), mirroring `buildRenderableShellTyped`.
    linetypeRaw: entity.linetype,
    attributes: mapBlockAttributeReferences(
      entity.blockAttributeReferences ?? [],
      // Codex PR #784 round 3 P2 — own-property-safe block-table read (a
      // MLEADER block-content name is raw DXF group-2 text).
      safeRecordLookup(blocks, raw.blockContentName),
    ),
    blocks,
    ctx,
    spaceId: entity.spaceId,
    parentEntityId: entity.id,
  });

  const geometry = toMultileaderSceneGeometry(result.primitives);
  return buildRenderableShell(entity, ctx, geometry, result.bbox);
}
